import { useState, type InputHTMLAttributes } from 'react';
import type { ConnectionState } from './realtime';

/** Live-connection indicator. Silent when healthy; loud only when it is not. */
export function ConnectionBadge({ state }: { state: ConnectionState }) {
  const label = {
    connected: 'Live',
    connecting: 'Connecting',
    reconnecting: 'Reconnecting',
    disconnected: 'Offline',
  }[state];

  const tone = {
    connected: 'text-(--color-good) border-(--color-good)',
    connecting: 'text-(--color-muted) border-(--color-line)',
    reconnecting: 'text-(--color-warn) border-(--color-warn)',
    disconnected: 'text-(--color-bad) border-(--color-bad)',
  }[state];

  return (
    <span className={`rounded border px-2 py-0.5 text-[10px] font-medium tracking-wide uppercase ${tone}`}>
      {label}
    </span>
  );
}

/**
 * A password field with a show/hide toggle.
 *
 * Masking is for the person behind you, not for the person typing. Somebody who
 * cannot see what they typed retypes it, gets it wrong again, and then resets a
 * password that was never wrong — the reset flow's busiest customer is the
 * masked field. The toggle is a button, not an icon, so it is reachable by
 * keyboard and read by a screen reader; and it is `aria-pressed` rather than a
 * changing label, so the announcement is "Show password, pressed" and not a
 * second control appearing from nowhere.
 */
export function PasswordInput({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  const [shown, setShown] = useState(false);
  return (
    <div className="relative mt-1.5">
      <input
        {...rest}
        type={shown ? 'text' : 'password'}
        className={`w-full rounded border border-(--color-line) bg-transparent py-2 pr-16 pl-3 text-sm outline-none focus:border-(--color-brand) ${className ?? ''}`}
      />
      <button
        type="button"
        aria-pressed={shown}
        aria-label="Show password"
        onClick={() => setShown((v) => !v)}
        className="absolute inset-y-0 right-0 px-3 text-xs font-medium text-(--color-muted) normal-case tracking-normal hover:text-(--color-ink)"
      >
        {shown ? 'Hide' : 'Show'}
      </button>
    </div>
  );
}

export function SignInForm({
  title, subtitle, isBusy, error, onSubmit, footer,
}: {
  title: string;
  subtitle: string;
  isBusy: boolean;
  error: string | null;
  onSubmit: (email: string, password: string) => void;
  /** Anything below the button — the forgot-password link, in practice. */
  footer?: React.ReactNode;
}) {
  return (
    <div className="flex min-h-full items-center justify-center px-6 py-16">
      <form
        className="w-full max-w-sm rounded border border-(--color-line) bg-(--color-surface) p-8"
        onSubmit={(event) => {
          event.preventDefault();
          const form = new FormData(event.currentTarget);
          onSubmit(String(form.get('email') ?? ''), String(form.get('password') ?? ''));
        }}
      >
        <h1 className="text-lg font-semibold tracking-tight">{title}</h1>
        <p className="mt-1 text-sm text-(--color-muted)">{subtitle}</p>

        {error && (
          <p className="mt-4 rounded border border-(--color-bad) px-3 py-2 text-xs text-(--color-bad)">
            {error}
          </p>
        )}

        <label className="mt-5 block text-xs font-medium tracking-wide text-(--color-muted) uppercase">
          Email
          <input
            name="email" type="email" required autoComplete="username"
            className="mt-1.5 w-full rounded border border-(--color-line) bg-transparent px-3 py-2 text-sm outline-none focus:border-(--color-brand)"
          />
        </label>

        <label className="mt-4 block text-xs font-medium tracking-wide text-(--color-muted) uppercase">
          Password
          <PasswordInput name="password" required autoComplete="current-password" />
        </label>

        <button
          type="submit" disabled={isBusy}
          className="mt-6 w-full rounded bg-(--color-brand-strong) px-3 py-2 text-sm font-medium text-(--color-brand-ink) disabled:opacity-50"
        >
          {isBusy ? 'Signing in…' : 'Sign in'}
        </button>

        {footer && <div className="mt-4 text-center text-xs">{footer}</div>}
      </form>
    </div>
  );
}
